import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaLock, FaEye, FaEyeSlash, FaCheck } from 'react-icons/fa';
import Layout from '../components/layout/Layout';
import logo from '../assets/logo.png';
import { showToast } from '../components/ui/Toast';
import './ForgotPassword.css';
import api from '../services/api';

const NewPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { email, token } = location.state || {};

  useEffect(() => {
    if (!email || !token) {
      navigate('/forgot-password');
    }
  }, [email, token, navigate]);

  const checkPasswordStrength = (pwd) => {
    if (!pwd) return '';
    if (pwd.length < 6) return 'weak';
    if (pwd.length < 8) return 'medium';
    
    const hasUpperCase = /[A-Z]/.test(pwd);
    const hasLowerCase = /[a-z]/.test(pwd);
    const hasNumbers = /\d/.test(pwd);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);
    
    const strength = [hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
    
    if (strength >= 3 && pwd.length >= 8) return 'strong';
    if (strength >= 2) return 'medium';
    return 'weak';
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setPasswordStrength(checkPasswordStrength(pwd));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);

      const response = await api.post('/users/reset-password/', {
        email,
        token,
        new_password: password,
        confirm_password: confirmPassword
      });

      if (response.data.success) {
        showToast.success('Password reset successfully!');
        navigate('/password-reset-success', { state: { email } });
      } else {
        throw new Error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || 
                      error.response?.data?.error ||
                      error.message || 
                      'Failed to reset password. Please try again.';
      setError(errorMsg);
      showToast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const getStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'strong': return '#10b981';
      default: return '#d1d5db';
    }
  };

  const getStrengthWidth = () => {
    switch (passwordStrength) {
      case 'weak': return '33%';
      case 'medium': return '66%';
      case 'strong': return '100%';
      default: return '0%';
    }
  };

  return (
    <Layout>
      <div className="forgot-password-page-container">
        <div className="forgot-password-split-screen">
          <div className="forgot-password-left-panel">
            <div className="forgot-password-content">
              <img src={logo} alt="UniTrade Logo" className="forgot-password-logo" />
              <h2>Create New Password</h2>
              <p>Choose a strong password to secure your account.</p>
            </div>
          </div>
          
          <div className="forgot-password-right-panel">
            <div className="forgot-password-form-container">
              <h3>Set New Password</h3>
              <p className="forgot-subtitle">Enter your new password below</p>
              
              {error && <div className="error-message">{error}</div>}
              
              <form onSubmit={handleSubmit} className="forgot-password-form">
                <div className="input-group-forgot">
                  <label htmlFor="password">New Password</label>
                  <div className="input-with-icon">
                    <FaLock className="input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={handlePasswordChange}
                      disabled={isLoading}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {password && (
                    <div className="password-strength-container">
                      <div className="password-strength-bar">
                        <div 
                          className="password-strength-fill"
                          style={{ 
                            width: getStrengthWidth(),
                            backgroundColor: getStrengthColor(),
                            transition: 'all 0.3s ease'
                          }}
                        />
                      </div>
                      <span 
                        className="password-strength-text"
                        style={{ color: getStrengthColor() }}
                      >
                        {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                      </span>
                    </div>
                  )}
                </div>

                <div className="input-group-forgot">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="input-with-icon">
                    <FaLock className="input-icon" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {confirmPassword && password === confirmPassword && (
                    <div className="password-match-indicator">
                      <FaCheck style={{ color: '#10b981' }} /> Passwords match
                    </div>
                  )}
                </div>

                <div className="password-requirements">
                  <p className="forgot-subtitle mb-2">Password must:</p>
                  <ul>
                    <li className={password.length >= 6 ? 'requirement-met' : ''}>
                      Be at least 6 characters long
                    </li>
                    <li className={/[A-Z]/.test(password) ? 'requirement-met' : ''}>
                      Contain at least one uppercase letter (recommended)
                    </li>
                    <li className={/[a-z]/.test(password) ? 'requirement-met' : ''}>
                      Contain at least one lowercase letter (recommended)
                    </li>
                    <li className={/\d/.test(password) ? 'requirement-met' : ''}>
                      Contain at least one number (recommended)
                    </li>
                  </ul>
                </div>
                
                <button 
                  type="submit" 
                  className="forgot-btn"
                  disabled={isLoading || !password || !confirmPassword || password !== confirmPassword}
                >
                  {isLoading ? (
                    'Resetting Password...'
                  ) : (
                    <>
                      Reset Password <FaCheck className="ms-2" />
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NewPassword;
